import {
  Stack,
  Flex,
  Box,
  Center,
  Image,
  AspectRatio,
  CloseButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { Display03, Paragraph02 } from "~/components/Texts";
import { useTheme } from "@chakra-ui/react";
import { RiImageAddLine } from "react-icons/ri";
import OnboardingNavigation from "~/components/onboarding/OnboardingNavigation";
import { useState, useEffect } from "react";
import FilePicker from "~/components/FilePicker";

export default function OnboardingDomain({
  onboardingData,
  onBack,
  onComplete,
  onSkip,
  showBack,
}) {
  const [localImageFile, setLocalImageFile] = useState(
    onboardingData.localLogoFile ? onboardingData.localLogoFile : null
  );
  const [localImagePreview, setLocalImagePreview] = useState(null);

  useEffect(() => {
    if (localImageFile !== null) {
      URL.revokeObjectURL(localImagePreview);
      // create the image preview for the local image file
      const newPreview = URL.createObjectURL(localImageFile);
      setLocalImagePreview(newPreview);
    } else {
      URL.revokeObjectURL(localImagePreview);
      setLocalImagePreview(null);
    }

    // free memory whenever this component is unmounted
    return () => URL.revokeObjectURL(localImagePreview);
  }, [localImageFile]);

  const handleChange = (imageFile) => {
    setLocalImageFile(imageFile);
  };

  const handleComplete = () => {
    onComplete({ localLogoFile: localImageFile }, null);
  };

  const handleRemove = () => {
    setLocalImageFile(null);
  };

  const handleSkip = () => {
    onSkip();
  };

  const isStepReady = () => {
    return localImageFile !== null;
  };

  return (
    <Flex flex="1" direction="column">
      <Box flex="1">
        <Stack
          height="100%"
          width={{ mobile: "100%", desktop: "100%" }}
          spacing="8"
        >
          <Display03>Logo de tu marca</Display03>
          <Stack
            flex="1"
            direction={{ mobile: "column", desktop: "row" }}
            align={{ mobile: "center", desktop: "start  " }}
          >
            <Box
              flex={{ mobile: 1, desktop: 3 }}
              pr={{ mobile: "0", desktop: "8" }}
              pb={{ mobile: "8", desktop: "0" }}
            >
              <Stack spacing="8">
                <Paragraph02>
                  El logo representa tu marca y se utiliza en diversos sitios de
                  tu tienda, como así también en comunicaciones con tus
                  clientes.
                </Paragraph02>
                <Paragraph02>
                  <b>Nota:</b> El formato de tu logo puede ser .jpg, .png o .gif
                </Paragraph02>
                <Paragraph02>
                  Para una mejor visualización en tu tienda, recomendamos
                  utilizar imágenes cuadradas con una resolución mínima de
                  512x512 pixels.
                </Paragraph02>
              </Stack>
            </Box>
            <AspectRatio
              w="100%"
              flex={{ mobile: 1, desktop: 2 }}
              maxH="90%"
              maxW={{ mobile: "256px", desktop: "512px" }}
              ratio={1}
            >
              <Flex
                flex="1"
                width="100%"
                height="100%"
                borderRadius="brand"
                borderColor="action.primary"
                borderWidth="2px"
                backgroundColor={useColorModeValue(
                  "light.background.action",
                  "dark.background.action"
                )}
              >
                {localImagePreview === null && (
                  <ImagePicker
                    onImageFileSelected={(imageFile) => handleChange(imageFile)}
                  />
                )}
                {localImagePreview !== null && (
                  <ImagePreview
                    src={localImagePreview}
                    onImageFileSelected={(imageFile) => handleChange(imageFile)}
                    onRemoveImage={() => handleRemove()}
                  />
                )}
              </Flex>
            </AspectRatio>
          </Stack>
        </Stack>
      </Box>
      <Box>
        <OnboardingNavigation
          showBack={showBack}
          showSkip={!isStepReady()}
          readyToContinue={isStepReady()}
          onBack={() => onBack()}
          onNext={() => handleComplete()}
          onSkip={() => handleSkip()}
        />
      </Box>
    </Flex>
  );
}

function ImagePicker({ onImageFileSelected }) {
  const chakraTheme = useTheme();

  return (
    <FilePicker onChange={(imageFile) => onImageFileSelected(imageFile)}>
      <Center width="100%" height="100%" cursor="pointer">
        <RiImageAddLine
          color={useColorModeValue(
            chakraTheme.colors.light.brand.lite,
            chakraTheme.colors.dark.brand.lite
          )}
          size="64"
        />
      </Center>
    </FilePicker>
  );
}

function ImagePreview({ src, onImageFileSelected, onRemoveImage }) {
  return (
    <Box cursor="pointer">
      <FilePicker onChange={(imageFile) => onImageFileSelected(imageFile)}>
        <Stack
          direction="row"
          position="absolute"
          top="0"
          width="100%"
          height="100%"
        >
          <Box flex="1" />
          <CloseButton
            size="lg"
            color="action.secondary"
            onClick={(event) => {
              onRemoveImage();
              event.stopPropagation();
            }}
          />
        </Stack>
        <Center>
          <Image src={src} objectFit="contain " />
        </Center>
      </FilePicker>
    </Box>
  );
}
